import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { Bell, Check, Clock, AlertCircle, Phone, Calendar, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import ReminderPopup from './ReminderPopup';

const REMINDER_TYPE_ICONS = {
  follow_up: Calendar,
  call: Phone,
  meeting: Users,
};

const POLL_INTERVAL = 60000; // 60 seconds

export const NotificationBell = () => {
  const [reminders, setReminders] = useState({ overdue: [], upcoming: [], pendingCount: 0, overdueCount: 0 });
  const [activePopup, setActivePopup] = useState(null);
  const [open, setOpen] = useState(false);
  const [shownPopupIds, setShownPopupIds] = useState(new Set());
  const pollIntervalRef = useRef(null);
  
  const fetchDueReminders = useCallback(async () => {
    try {
      const response = await api.get('/reminders/due');
      setReminders(response.data);
      
      // Show popup for first overdue reminder not yet shown in this session
      if (response.data.overdue?.length > 0 && !activePopup) {
        const firstUnshown = response.data.overdue.find(r => !shownPopupIds.has(r.id));
        if (firstUnshown) {
          setActivePopup(firstUnshown);
          setShownPopupIds(prev => new Set([...prev, firstUnshown.id]));
          
          // Request browser notification permission and show notification
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(`Reminder: ${firstUnshown.title}`, {
              body: `${firstUnshown.leadName} - ${firstUnshown.description || 'No description'}`,
              icon: '/favicon.ico',
              tag: firstUnshown.id,
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
    }
  }, [activePopup, shownPopupIds]);
  
  useEffect(() => {
    // Request notification permission on mount
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    // Initial fetch - using a flag to avoid setState warning
    let isMounted = true;
    const initialFetch = async () => {
      if (isMounted) {
        await fetchDueReminders();
      }
    };
    initialFetch();
    
    // Set up polling
    pollIntervalRef.current = setInterval(fetchDueReminders, POLL_INTERVAL);
    
    return () => {
      isMounted = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handleComplete = async (reminderId) => {
    try {
      await api.post(`/reminders/${reminderId}/complete`);
      toast.success('Reminder completed');
      fetchDueReminders();
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };
  
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date - now;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 0) {
      const overdueMins = Math.abs(diffMins);
      if (overdueMins < 60) return `${overdueMins}m overdue`;
      if (overdueMins < 1440) return `${Math.floor(overdueMins / 60)}h overdue`;
      return `${Math.floor(overdueMins / 1440)}d overdue`;
    }
    
    if (diffMins < 60) return `in ${diffMins}m`;
    if (diffMins < 1440) return `in ${Math.floor(diffMins / 60)}h`;
    return date.toLocaleDateString('en-IN', { dateStyle: 'short' });
  };
  
  const totalPending = reminders.pendingCount || 0;
  const hasOverdue = reminders.overdueCount > 0;
  
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            data-testid="notification-bell-btn"
          >
            <Bell className={`h-5 w-5 ${hasOverdue ? 'text-red-500' : ''}`} />
            {totalPending > 0 && (
              <Badge 
                variant={hasOverdue ? 'destructive' : 'default'}
                className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
              >
                {totalPending > 9 ? '9+' : totalPending}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-80 p-0" align="end" data-testid="notification-popover">
          <div className="p-3 border-b">
            <h4 className="font-semibold text-sm">Reminders</h4>
            <p className="text-xs text-muted-foreground">
              {hasOverdue 
                ? `${reminders.overdueCount} overdue • ${reminders.upcoming?.length || 0} upcoming`
                : `${totalPending} pending reminders`
              }
            </p>
          </div>
          
          <ScrollArea className="h-[300px]">
            {reminders.overdue?.length === 0 && reminders.upcoming?.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No pending reminders</p>
              </div>
            ) : (
              <div className="divide-y">
                {/* Overdue reminders */}
                {reminders.overdue?.map((reminder) => {
                  const TypeIcon = REMINDER_TYPE_ICONS[reminder.type] || Calendar;
                  return (
                    <div
                      key={reminder.id}
                      className="p-3 hover:bg-muted/50 transition-colors"
                      data-testid={`reminder-item-${reminder.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-1.5 rounded-full bg-red-100">
                          <TypeIcon className="h-4 w-4 text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium truncate">{reminder.title}</p>
                            <Badge variant="destructive" className="text-xs h-5">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Overdue
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{reminder.leadName}</p>
                          <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
                            <Clock className="h-3 w-3" />
                            {formatTime(reminder.scheduledAt)}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0"
                          onClick={() => handleComplete(reminder.id)}
                          data-testid={`complete-btn-${reminder.id}`}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                
                {/* Upcoming reminders */}
                {reminders.upcoming?.map((reminder) => {
                  const TypeIcon = REMINDER_TYPE_ICONS[reminder.type] || Calendar;
                  return (
                    <div
                      key={reminder.id}
                      className="p-3 hover:bg-muted/50 transition-colors"
                      data-testid={`reminder-item-${reminder.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-1.5 rounded-full bg-primary/10">
                          <TypeIcon className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{reminder.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{reminder.leadName}</p>
                          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {formatTime(reminder.scheduledAt)}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0"
                          onClick={() => handleComplete(reminder.id)}
                          data-testid={`complete-btn-${reminder.id}`}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
          
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              className="w-full text-sm"
              onClick={() => {
                setOpen(false);
                window.location.href = '/reminders';
              }}
              data-testid="view-all-reminders-btn"
            >
              View All Reminders
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      
      {/* Active reminder popup - DISABLED */}
    </>
  );
};

export default NotificationBell;
